// DUCKS pattern
import { createAction, createSelector, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "@src/ducks/store";
import _ from "lodash";
import moment from "moment";
import { deleteNotification, readNotification, updateMissions } from "utils/filter-helper";
import { MAavatars, MTierLevel } from "utils/mockdata";
import { filterShopProducts } from "utils/transform-helper";
import { ChangeScreen, ErrorValue, HourlyReward, Product } from "../types";

export interface InitialState {
  loading: boolean;
  lobbysuccess: boolean;
  error: ErrorValue;
  serverTime: number;
  jadeAmount: number;
  randomRewards: any[];
  missions: any[];
  missionEndDate: string;
  missionStatus: string;
  avatarTier: any;
  products: Product[];
  hourlyReward: HourlyReward;
  notifications: any[];
  changeScreen: ChangeScreen;

  claiming: boolean;
  claimingSuccess: boolean;
  claiming3hoursBonusSuccess: boolean;

  purchasing: boolean;
  purchaseSuccess: any;
  enableHotdeals: boolean;

  oneTimeShowFavourite: boolean;
  oneTimeShowHowToPlay: boolean;
  popupScreenToDisplay: any[];
}

export const initialState: InitialState = {
  loading: false,
  lobbysuccess: false,
  error: {} as ErrorValue,
  serverTime: 0,
  jadeAmount: 0,
  avatarTier: { displayName: "" },
  hourlyReward: {} as HourlyReward,
  notifications: [] as any,
  changeScreen: { screen: "main", gmode: "" } as ChangeScreen,
  products: [] as any,
  missions: [] as any,
  missionEndDate: "",
  claiming: false,
  enableHotdeals: false,
  claimingSuccess: false,
  claiming3hoursBonusSuccess: false,
} as InitialState;

// Slice
export const lobbySlice = createSlice({
  name: "lobby",
  initialState,
  reducers: {
    lobbyRequest: (state) => {
      state.loading = true;
      state.lobbysuccess = false;
      state.error = {} as ErrorValue;
    },
    lobbySuccess: (state) => {
      state.error = {} as ErrorValue;
      state.loading = false;
      state.lobbysuccess = true;
    },
    lobbyFailure: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    lobbyReset: () => {
      return initialState;
    },

    // SERVER TIME
    serverTime: (state, action) => {
      state.serverTime = action.payload;
    },
    
    jadeAmount: (state, action) => {
      state.jadeAmount = action.payload;
    },
    randomRewards: (state, action) => {
      state.randomRewards = action.payload;
    },
    missions: (state, action) => {
      state.missionEndDate = action.payload.endAt;
      state.missions = action.payload.userMissions;
      state.missionStatus = action.payload.status;
    },
    updateMissions: (state, action) => {
      state.missions = updateMissions(state.missions, action.payload);
    },
    avatarTier: (state, action) => {
      state.avatarTier = {...state.avatarTier, ...action.payload};
    },
    products: (state, action) => {
      state.products = action.payload;
    },
    hourlyReward: (state, action) => {
      state.hourlyReward = action.payload;
    },
    notifications: (state, action) => {
      state.notifications = _.chain(action.payload)
        .orderBy(["id"], ["desc"])
        .value();
    },
    updateNotifications: (state, action) => {
      if(action.payload.status === "READ"){
        state.notifications = readNotification(state.notifications, action.payload);
      }else if(action.payload.status === "DELETE"){
        state.notifications = deleteNotification(state.notifications, action.payload);
      }
    },
    changeScreen: (state, action) => {
      state.changeScreen = action.payload;
    },

    setEnableHotdeals: (state) => {
      state.enableHotdeals = true;
    },

    oneTimeShowFavourite: (state, action) => {
      state.oneTimeShowFavourite = action.payload;
    },
    oneTimeShowHowToPlay: (state, action) => {
      state.oneTimeShowHowToPlay = action.payload;
    },
    popupScreenToDisplay: (state, action) => {
      state.popupScreenToDisplay = action.payload;
    },
    updatePopupScreenToDisplay: (state) => {
      if (state.popupScreenToDisplay.length > 0) {
        // Remove the element at index 0
        state.popupScreenToDisplay.shift();
      }
    },
  },
});

// Actions
export const lobbyActions = {
  lobbyRequest: lobbySlice.actions.lobbyRequest,
  lobbySuccess: lobbySlice.actions.lobbySuccess,
  lobbyFailure: lobbySlice.actions.lobbyFailure,
  lobbyReset: lobbySlice.actions.lobbyReset,

  // SERVER TIME
  serverTime: lobbySlice.actions.serverTime,
  jadeAmount: lobbySlice.actions.jadeAmount,
  randomRewards: lobbySlice.actions.randomRewards,
  missions: lobbySlice.actions.missions,
  updateMissions: lobbySlice.actions.updateMissions,
  avatarTier: lobbySlice.actions.avatarTier,
  products: lobbySlice.actions.products,
  hourlyReward: lobbySlice.actions.hourlyReward,
  notifications: lobbySlice.actions.notifications,
  fetchNotifications: createAction(`${lobbySlice.name}/fetchNotifications`),
  updateNotifications: lobbySlice.actions.updateNotifications,
  changeScreen: lobbySlice.actions.changeScreen,

  checkTierRequest: createAction(`${lobbySlice.name}/checkTierRequest`),

  //CHECK BALANCE
  balanceRequest: createAction(`${lobbySlice.name}/balanceRequest`),
  setEnableHotdeals: lobbySlice.actions.setEnableHotdeals,
  checkGoldenBallAmount: createAction(`${lobbySlice.name}/checkGoldenBallAmount`),

  oneTimeShowFavourite: lobbySlice.actions.oneTimeShowFavourite,
  oneTimeShowHowToPlay: lobbySlice.actions.oneTimeShowHowToPlay,
  dismissOneTimeShow: createAction(`${lobbySlice.name}/dismissOneTimeShow`),

  popupScreenToDisplay: lobbySlice.actions.popupScreenToDisplay,
  updatePopupScreenToDisplay: lobbySlice.actions.updatePopupScreenToDisplay,
};

// Selectors
export const selectedLobbyLoading = (state: RootState) => state.lobby.loading;
export const selectedLobbyFailed = (state: RootState) => state.lobby.error;
export const selectedLobbySuccess = (state: RootState) => state.lobby.lobbysuccess;
export const selectedGoldenBallAmount = (state: RootState) => state.lobby.jadeAmount;
export const selectedRandomRewards = (state: RootState) => state.lobby.randomRewards;
export const selectedMissions = (state: RootState) => state.lobby.missions;
export const selectedProducts = (state: RootState) => state.lobby.products;
export const selectedNotifications = (state: RootState) => state.lobby.notifications;
export const selectedChangeScreen = (state: RootState) => state.lobby.changeScreen;
export const selectedClaiming = (state: RootState) => state.lobby.claiming;
export const selectedClaimingSuccess = (state: RootState) => state.lobby.claimingSuccess;
export const selectedClaiming3HoursBonusSuccess = (state: RootState) => state.lobby.claiming3hoursBonusSuccess;
export const selectedPurchaseLoad = (state: RootState) => state.lobby.purchasing;
export const selectedPurchaseData = (state: RootState) => state.lobby.purchaseSuccess;
export const selectedEnableHotdeals = (state: RootState) => state.lobby.enableHotdeals;
export const selectedMissionEndDate = (state: RootState) => state.lobby.missionEndDate;
export const selectedOneTimeShowHowToPlay = (state: RootState) => state.lobby.oneTimeShowHowToPlay;
export const selectedPopupScreenToDisplay= (state: RootState) => state.lobby.popupScreenToDisplay;

export const selectedMissionClaimed = createSelector(
  (state: RootState) => state.lobby.missionStatus,
  (state) => state === "REWARD_CLAIMED"
);

export const selectedMissionCompleted = createSelector(
  (state: RootState) => state.lobby.missionStatus,
  (state) => state === "COMPLETED"
);

export const selectedMissionReadyToClaim = createSelector(
  (state: any) => state.lobby.missions,
  (state: any) => state.lobby.missionStatus,
  (missions, missionStatus) => { 
    const result = missions.map((item: any) => {
      if (item.status === "REWARD_CLAIMED") {
        return true;
      } else {
        return null;
      }
    });

    return !result.includes(null) && missionStatus === "COMPLETED";
  }
);

export const selectedHourlyReward = createSelector(
  (state: RootState) => state.lobby.hourlyReward,
  (state) => {
    const start = moment(state.initialDate, "YYYY-MM-DD HH:mm:ss");
    const end = moment(state.claimDate, "YYYY-MM-DD HH:mm:ss");
    const seconds = moment.duration(end.diff(start)).asSeconds();

    return {
      id: state.id,
      timer: seconds > 0 ? seconds : 0,
    };
  }
);

export const selectedNotificationsUnread = createSelector(
  (state: RootState) => state.lobby.notifications,
  (state) => state.find((item: any) => item?.status === "UNREAD")
);

export const selectedActiveAvatarWB = createSelector(
  (state: RootState) => state.lobby.avatarTier,
  (state) => MAavatars[state.avatarID > 0 ? state.avatarID - 1 : 0].body,
);
export const selectedAvatarTier = createSelector(
  (state: any) => state.lobby.avatarTier,
  (state) => ({
    ...state,
    tierAvatar: MTierLevel[state.tierID - 1]?.tier_icon,
  })
);
export const selectedUserTierID = createSelector(
  (state: RootState) => state.lobby.avatarTier,
  (state) => state.tierID,
);

export const selectedShopItems = createSelector(
  (state: RootState) => state.lobby.products,
  (state) => filterShopProducts(state || []),
);

export const selectedServerTime = createSelector(
  (state: any) => state.lobby.serverTime,
  (state) => {
    if (state === 0) {
      return 0;
    }

    const today: Date = new Date(state);

    today.setDate(today.getDate() + 1);

    const date = moment(state).format("YYYY-MM-DD HH:mm:ss");
    const nextday = moment(today).format("YYYY-MM-DD 00:00:00");
    const seconds = moment.duration(moment(nextday).diff(date)).asSeconds();

    return seconds;
  }
);

export const selectedMissionHasKeys = createSelector(
  (state: any) => state.lobby.missions,
  (state) => {
    const result = state.map((item: any) => {
      if (item.hasKey) {
        return "HAS_KEY";
      } else if (item.completedDate) {
        return true;
      } else {
        return null;
      }
    });

    return result.includes("HAS_KEY");
  }
);

// Reducer
export default lobbySlice.reducer;
